import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  DRINK_PROFILE_LABELS,
  URGENCY_DESCRIPTIONS,
  URGENCY_LABELS,
  WINE_TYPE_LABELS,
  formatPosition,
  idadeSemAnaliseQuePedeAtencao,
  nomeCompleto,
} from '@adega/shared';
import { AdicionarIguais } from '../components/AdicionarIguais.js';
import { CellarGrid } from '../components/CellarGrid.js';
import { AiConfigBanner, ErrorBox, Loading } from '../components/Feedback.js';
import { PageHeader } from '../components/PageHeader.js';
import {
  useAiStatus,
  useBottle,
  useCellar,
  useCellars,
  useConsumeBottle,
  useDeleteBottle,
  useEnrichBottle,
  useOcuparVaga,
  useUnconsumeBottle,
  useUpdateBottle,
  useWaiting,
} from '../api/queries.js';
import { hoje, momentoDoConsumo } from '../lib/datas.js';
import { emReais } from '../lib/dinheiro.js';

export function BottleDetail() {
  const { id } = useParams();
  const bottleId = Number(id);
  const navigate = useNavigate();

  const { data: bottle, isLoading, error } = useBottle(bottleId);
  const { data: cellars } = useCellars();
  /**
   * Mover pode trocar de adega, não só de prateleira. O backend sempre aceitou
   * (`updateBottle` trata `cellarId`); era a tela que não oferecia — e garrafa
   * salva na adega errada ficava presa lá, sem saída pelo app.
   */
  const [adegaDoMover, setAdegaDoMover] = useState<number | null>(null);
  const { data: cellar } = useCellar(adegaDoMover ?? bottle?.cellarId ?? undefined);
  const { data: aiStatus } = useAiStatus();
  const enriquecer = useEnrichBottle(bottleId);
  const consumir = useConsumeBottle(bottleId);
  const atualizar = useUpdateBottle(bottleId);
  const excluir = useDeleteBottle(bottleId);
  /**
   * A fila entra aqui porque beber é o momento em que uma vaga abre. Sem isso
   * a vaga abria em silêncio e a fila só andava se a pessoa lembrasse dela.
   *
   * Vai com a adega da vaga: a ordem certa depende do móvel. Numa adega de
   * passagem, a vaga é de quem deve ser bebido antes — sem isso o app chegou a
   * oferecer uma vaga da adega de madeira, sem climatização, a um Gran Reserva
   * de guarda longa.
   */
  const { data: fila } = useWaiting(bottle?.cellarId ?? undefined);
  const ocuparVaga = useOcuparVaga();

  const [movendo, setMovendo] = useState(false);
  const [consumindo, setConsumindo] = useState(false);
  const [confirmandoExclusao, setConfirmandoExclusao] = useState(false);
  const [adicionandoIguais, setAdicionandoIguais] = useState(false);
  const [iguaisAdicionadas, setIguaisAdicionadas] = useState<number | null>(null);
  const [nota, setNota] = useState<number | null>(null);
  const [quando, setQuando] = useState(hoje());
  const [comentario, setComentario] = useState('');
  const [acabouDeBeber, setAcabouDeBeber] = useState(false);
  const [vagaPreenchidaPor, setVagaPreenchidaPor] = useState<string | null>(null);
  const desfazer = useUnconsumeBottle(bottleId);

  if (isLoading) return <Loading />;
  if (error || !bottle) {
    return (
      <div>
        <PageHeader titulo="Garrafa" voltar />
        {error ? <ErrorBox erro={error} /> : null}
      </div>
    );
  }

  const ficha = [
    ['Produtor', bottle.producer],
    ['Safra', bottle.vintage?.toString() ?? 'Sem safra'],
    ['Tipo', bottle.type ? WINE_TYPE_LABELS[bottle.type] : null],
    ['Uva', bottle.grapeVariety],
    ['Região', [bottle.region, bottle.country].filter(Boolean).join(', ') || null],
    ['Volume', bottle.volumeMl ? `${bottle.volumeMl} ml` : null],
    [
      'Teor alcoólico',
      bottle.alcoholPercent !== null ? `${bottle.alcoholPercent.toLocaleString('pt-BR')}%` : null,
    ],
    ['Rótulo', bottle.labelDetails],
    // A compra fecha a ficha: é o que a pessoa lembra depois ("quanto paguei
    // mesmo?"), e não tem nada a ver com o que está escrito no rótulo.
    ['Valor pago', bottle.purchasePrice !== null ? emReais(bottle.purchasePrice) : null],
    ['Comprado em', bottle.purchasePlace],
  ].filter(([, valor]) => Boolean(valor)) as Array<[string, string]>;

  // Numeração e tiragem vêm impressas no rótulo: é fato, não opinião da IA, e é o
  // tipo de detalhe que faz a garrafa ser especial — por isso ganha destaque.
  const producao = bottle.productionSize?.toLocaleString('pt-BR');
  const destaqueNumeracao = bottle.bottleNumber
    ? `Garrafa numerada · nº ${bottle.bottleNumber}${producao ? ` de ${producao}` : ''}`
    : producao
      ? `Produção limitada · ${producao} garrafas`
      : null;

  // Consumida não precisa de aviso: já saiu da adega.
  const idadeSemAnalise =
    bottle.status === 'in_cellar' ? idadeSemAnaliseQuePedeAtencao(bottle) : null;

  /*
   * Nunca analisada. `aiEnrichedAt` e não `drinkFrom`: garrafa pode ter janela
   * copiada de uma igual (ver `idsDasIguais`) sem nunca ter passado pela IA, e
   * essa já está servida — pedir análise ali seria gastar à toa.
   */
  const semAnalise = bottle.aiEnrichedAt === null && bottle.drinkUntil === null;

  // A análise lê o contrarrótulo antes quando a foto ainda não passou pela
  // leitura completa — leva mais tempo, e a tela diz isso.
  const vaiLerRotulo = bottle.photoUrl !== null && bottle.producerSheet === null;

  // Beber so abre vaga quando a garrafa ocupava uma. Quem esta esperando vaga —
  // seja fora das adegas, seja dentro de uma sem posicao definida — sai da fila
  // sem liberar espaco para ninguem.
  const ocupaVaga = bottle.shelfId !== null && bottle.position !== null;

  // A vaga que acabou de abrir, com nome e sobrenome. O `cellar` ja esta
  // carregado: e o mesmo que o "mover" usa, e garrafa bebida guarda o vinculo
  // com a adega.
  const vagaAberta =
    bottle.consumedFrom !== null && bottle.cellarId !== null
      ? { ...bottle.consumedFrom, cellarId: bottle.cellarId }
      : null;
  const prateleiraDaVaga = cellar?.shelves.find((p) => p.id === vagaAberta?.shelfId) ?? null;
  const rotuloDaVaga =
    vagaAberta !== null && prateleiraDaVaga !== null
      ? formatPosition(prateleiraDaVaga.label, vagaAberta.position, cellar?.name ?? null)
      : null;
  // Quem a fila diz que perde mais esperando. A ordem e do servidor
  // (`domain/fila.ts`), nao da tela: aqui so se pega o primeiro.
  const primeiroDaFila = fila?.[0] ?? null;

  const situacao =
    bottle.status === 'consumed'
      ? `Consumida em ${new Date(bottle.consumedAt ?? '').toLocaleDateString('pt-BR')}`
      : (bottle.positionLabel ?? 'Sem posição definida');

  return (
    <div className="space-y-4">
      <PageHeader
        titulo={nomeCompleto(bottle)}
        subtitulo={situacao}
        voltar
        acao={
          <Link to={`/garrafa/${bottle.id}/editar`} className="btn-ghost px-3 text-sm">
            ✏️ Editar
          </Link>
        }
      />

      {/* Celular: foto grande em cima e ficha embaixo. Lado a lado, a ficha
          ficava espremida em ~200px e quebrava "Luján de Cuyo, Mendoza…". */}
      <div className="card flex flex-col gap-4 sm:flex-row">
        {bottle.photoUrl ? (
          // Toque abre a foto em tamanho real — útil para ler letra miúda do rótulo.
          <a
            href={bottle.photoUrl}
            target="_blank"
            rel="noreferrer"
            className="mx-auto block h-72 w-48 shrink-0 overflow-hidden rounded-xl bg-adega-surface-2 sm:mx-0 sm:h-40 sm:w-28"
          >
            <img src={bottle.photoUrl} alt={`Rótulo de ${nomeCompleto(bottle)}`} className="h-full w-full object-cover" />
          </a>
        ) : (
          <span
            aria-hidden
            className="mx-auto grid h-40 w-28 shrink-0 place-items-center rounded-xl bg-adega-surface-2 text-4xl opacity-40 sm:mx-0"
          >
            🍾
          </span>
        )}

        <div className="min-w-0 flex-1 space-y-3">
          {destaqueNumeracao ? (
            <p className="inline-block rounded-full border border-adega-gold/40 bg-adega-gold/10 px-3 py-1 text-xs font-medium text-adega-gold">
              {destaqueNumeracao}
            </p>
          ) : null}

          <dl className="grid grid-cols-[auto_1fr] content-start gap-x-4 gap-y-2 text-sm">
            {ficha.map(([rotulo, valor]) => (
              <div key={rotulo} className="contents">
                <dt className="text-adega-muted">{rotulo}</dt>
                <dd className="min-w-0 break-words">{valor}</dd>
              </div>
            ))}
          </dl>
        </div>
      </div>

      <section className="card">
        <h2 className="font-semibold">{URGENCY_LABELS[bottle.urgency]}</h2>
        <p className="mt-1 text-sm text-adega-muted">{URGENCY_DESCRIPTIONS[bottle.urgency]}</p>

        {idadeSemAnalise !== null ? (
          <p className="mt-2 rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-sm text-amber-200">
            Safra {bottle.vintage}: {idadeSemAnalise} anos, idade em que muitos vinhos deste estilo já
            estão no fim da janela. Vale analisar antes que passe do ponto.
          </p>
        ) : null}

        {/* Vinho jovem não tem "guarda" nem "pico" a mostrar: o pico dele é o
            começo da janela, e dizer "pico em 2025" sugeriria uma evolução que
            não existe. O que importa é até quando beber. Garrafa analisada antes
            do perfil existir continua como antes. */}
        {bottle.drinkFrom || bottle.drinkUntil ? (
          bottle.drinkProfile === 'beber_jovem' ? (
            <p className="mt-2 text-sm">
              {DRINK_PROFILE_LABELS.beber_jovem}
              {bottle.drinkUntil ? (
                <span className="text-adega-gold"> · melhor até {bottle.drinkUntil}</span>
              ) : null}
            </p>
          ) : (
            <p className="mt-2 text-sm">
              {bottle.drinkProfile ? DRINK_PROFILE_LABELS[bottle.drinkProfile] : 'Janela de guarda'}:{' '}
              {bottle.drinkFrom ?? '?'} – {bottle.drinkUntil ?? '?'}
              {bottle.drinkPeak ? (
                <span className="text-adega-gold"> · pico em {bottle.drinkPeak}</span>
              ) : null}
            </p>
          )
        ) : null}

        {bottle.drinkNote ? (
          <p className="mt-1 text-sm text-adega-muted">{bottle.drinkNote}</p>
        ) : null}

        {bottle.agingGuidance ? (
          <p className="mt-3 text-sm leading-relaxed">{bottle.agingGuidance}</p>
        ) : null}

        {bottle.pairingNotes ? (
          <div className="mt-3 rounded-xl bg-adega-surface-2 p-3 text-sm leading-relaxed">
            <p className="mb-1 text-xs uppercase tracking-wide text-adega-muted">Harmonização</p>
            {bottle.pairingNotes}
          </div>
        ) : null}

        {/*
          Garrafa salva e nunca analisada: o aviso explica a CONSEQUÊNCIA, não só
          oferece a ação.

          Sem janela de guarda a garrafa nunca entra em "Beber antes" — a pessoa
          perde exatamente o motivo pelo qual o app existe, e perde em silêncio.
          A tela já era o destino certo (o cadastro navega para cá ao salvar);
          faltava ela dizer o que estava em jogo.

          Não aparece junto do aviso de safra antiga: aquele já pede a análise,
          com um argumento mais forte, e dois cartões pedindo a mesma coisa viram
          ruído.

          **E diz SÓ a consequência.** A primeira versão abria com "esta garrafa
          ainda não tem janela de guarda" — terceira vez que a tela afirmava a
          mesma coisa, depois do título da seção ("Sem informação de guarda") e
          da descrição logo abaixo dele. O que falta ali nunca foi repetir o
          diagnóstico: é dizer o que se perde.
        */}
        {semAnalise && idadeSemAnalise === null ? (
          <p className="mt-4 rounded-xl border border-adega-border bg-adega-surface-2 px-3 py-3 text-sm leading-relaxed text-adega-muted">
            Sem a janela, esta garrafa não entra na fila de{' '}
            <strong className="font-semibold text-adega-text">“Beber antes”</strong> — e é lá que o
            app avisa na hora de abrir.
          </p>
        ) : null}

        <div className="mt-4">
          {aiStatus?.enabled ? (
            <button
              type="button"
              /* Primário enquanto não há análise: é a ação que falta para a
                 garrafa valer o que ela pode valer. Depois volta a secundário —
                 reanalisar é exceção, não o caminho principal. */
              className={`${semAnalise ? 'btn-primary' : 'btn-secondary'} w-full`}
              disabled={enriquecer.isPending}
              onClick={() => void enriquecer.mutateAsync()}
            >
              {enriquecer.isPending
                ? vaiLerRotulo
                  ? 'Lendo o rótulo e analisando…'
                  : 'Analisando…'
                : bottle.aiEnrichedAt
                  ? '🤖 Atualizar análise da IA'
                  : '🤖 Analisar com IA'}
            </button>
          ) : (
            <AiConfigBanner contexto="A análise de guarda e harmonização depende da IA." />
          )}
        </div>

        {enriquecer.error ? (
          <div className="mt-3">
            <ErrorBox erro={enriquecer.error} />
          </div>
        ) : null}

        {bottle.aiEnrichedAt ? (
          <p className="mt-2 text-[11px] text-adega-muted">
            Análise gerada por {bottle.aiProvider}/{bottle.aiModel} em{' '}
            {new Date(bottle.aiEnrichedAt).toLocaleString('pt-BR')}
          </p>
        ) : null}
      </section>

      {bottle.backPhotoUrl ? (
        <section className="card">
          <h2 className="mb-2 font-semibold">Contrarrótulo</h2>
          <img
            src={bottle.backPhotoUrl}
            alt={`Contrarrótulo de ${nomeCompleto(bottle)}`}
            className="max-h-96 w-full rounded-xl object-contain"
          />
        </section>
      ) : null}

      {bottle.notes ? (
        <section className="card">
          <h2 className="mb-1 font-semibold">Suas observações</h2>
          <p className="text-sm leading-relaxed text-adega-muted">{bottle.notes}</p>
        </section>
      ) : null}

      {/* Garrafa bebida: o que ficou registrado, e a saída para quando foi
          engano. O desfazer não tem prazo — engano descoberto uma semana depois
          continua sendo engano. */}
      {bottle.status === 'consumed' ? (
        <section className="card space-y-3">
          <div>
            <h2 className="font-semibold">🍷 Você bebeu esta</h2>
            <p className="text-sm text-adega-muted">
              {new Date(bottle.consumedAt ?? '').toLocaleDateString('pt-BR', {
                day: 'numeric',
                month: 'long',
                year: 'numeric',
              })}
              {bottle.rating !== null ? ` · ${'★'.repeat(bottle.rating)}` : ''}
            </p>
          </div>

          {bottle.consumptionNote ? (
            <p className="text-sm leading-relaxed">{bottle.consumptionNote}</p>
          ) : null}

          {acabouDeBeber ? (
            <p className="rounded-lg bg-emerald-500/10 px-3 py-2 text-sm text-emerald-200">
              {/* Garrafa que estava esperando vaga nao ocupava posicao nenhuma:
                  dizer que "a posicao ficou livre" e prometer um espaco que nao
                  existe, e quem le vai procurar por ele na adega. */}
              {bottle.consumedFrom !== null
                ? 'Registrado. A posição na adega ficou livre.'
                : 'Registrado. Ela estava esperando vaga — a fila diminuiu.'}
            </p>
          ) : null}

          {/* O encontro entre beber e a fila. A vaga abre exatamente aqui, e é
              aqui que a fila tem alguma chance de andar — depois desta tela,
              ninguém mais lembra que ela existe. Só aparece quando a vaga é
              real e há alguém esperando. */}
          {acabouDeBeber && vagaAberta && rotuloDaVaga && primeiroDaFila && !vagaPreenchidaPor ? (
            <div className="space-y-2 rounded-lg border border-adega-gold/40 bg-adega-gold/10 p-3">
              <p className="text-sm font-medium">📦 Abriu uma vaga: {rotuloDaVaga}</p>
              <p className="text-sm text-adega-muted">
                Primeiro da fila:{' '}
                <span className="text-adega-text">{nomeCompleto(primeiroDaFila)}</span>
                {primeiroDaFila.drinkProfile
                  ? ` · ${DRINK_PROFILE_LABELS[primeiroDaFila.drinkProfile].toLocaleLowerCase('pt-BR')}`
                  : ''}
              </p>
              <button
                type="button"
                className="btn-primary w-full"
                disabled={ocuparVaga.isPending}
                onClick={async () => {
                  await ocuparVaga.mutateAsync({
                    bottleId: primeiroDaFila.id,
                    cellarId: vagaAberta.cellarId,
                    shelfId: vagaAberta.shelfId,
                    position: vagaAberta.position,
                  });
                  setVagaPreenchidaPor(nomeCompleto(primeiroDaFila));
                }}
              >
                {ocuparVaga.isPending ? 'Colocando…' : 'Colocar nesta vaga'}
              </button>
              {/* Saída para quando não é o primeiro da fila que ele quer
                  guardar: a fila sugere, quem decide é ele. */}
              <Link
                to="/esperando-vaga"
                className="block text-center text-xs text-adega-muted underline"
              >
                {fila && fila.length > 1
                  ? `Ver os outros ${fila.length - 1} da fila`
                  : 'Ver a fila'}
              </Link>
              {ocuparVaga.error ? <ErrorBox erro={ocuparVaga.error} /> : null}
            </div>
          ) : null}

          {vagaPreenchidaPor ? (
            <p className="rounded-lg bg-emerald-500/10 px-3 py-2 text-sm text-emerald-200">
              {vagaPreenchidaPor} saiu da fila e foi para {rotuloDaVaga}.
            </p>
          ) : null}

          <button
            type="button"
            className="btn-secondary w-full"
            disabled={desfazer.isPending}
            onClick={async () => {
              await desfazer.mutateAsync();
              setAcabouDeBeber(false);
              setNota(null);
              setComentario('');
            }}
          >
            {desfazer.isPending
              ? 'Devolvendo…'
              : bottle.cellarId === null
                ? '↩️ Não bebi — devolver para a fila'
                : '↩️ Não bebi — devolver para a adega'}
          </button>

          <p className="text-xs text-adega-muted">
            {bottle.consumedFrom !== null
              ? 'Volta para a mesma posição, se ela ainda estiver livre.'
              : bottle.cellarId !== null
                ? 'Volta para a adega, sem posição definida.'
                : 'Volta para a fila, esperando vaga.'}
          </p>

          {desfazer.error ? <ErrorBox erro={desfazer.error} /> : null}
        </section>
      ) : null}

      {bottle.status === 'in_cellar' ? (
        <section className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              className="btn-secondary"
              onClick={() => setMovendo((v) => !v)}
            >
              {movendo ? 'Cancelar' : 'Mover'}
            </button>
            <button
              type="button"
              className="btn-primary"
              onClick={() => setConsumindo((v) => !v)}
            >
              {consumindo ? 'Cancelar' : '🍷 Bebi esta'}
            </button>
          </div>

          <button
            type="button"
            className="btn-secondary w-full"
            onClick={() => {
              setIguaisAdicionadas(null);
              setAdicionandoIguais((v) => !v);
            }}
          >
            {adicionandoIguais ? 'Cancelar' : '＋ Tenho mais garrafas iguais'}
          </button>

          {adicionandoIguais ? (
            <AdicionarIguais
              bottle={bottle}
              cellar={bottle.cellarId ? cellar : null}
              onConcluir={(quantidade) => {
                setAdicionandoIguais(false);
                setIguaisAdicionadas(quantidade);
              }}
            />
          ) : null}

          {iguaisAdicionadas ? (
            <p className="rounded-lg bg-emerald-500/10 px-3 py-2 text-sm text-emerald-200">
              {iguaisAdicionadas === 1
                ? '1 garrafa igual adicionada.'
                : `${iguaisAdicionadas} garrafas iguais adicionadas.`}
            </p>
          ) : null}

          {movendo && cellar ? (
            <div className="space-y-2">
              {cellars && cellars.length > 1 ? (
                <div className="flex flex-wrap gap-1.5">
                  {cellars.map((item) => (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => setAdegaDoMover(item.id)}
                      className={`rounded-full px-3 py-1 text-xs transition ${
                        item.id === cellar.id
                          ? 'bg-adega-wine text-white'
                          : 'border border-adega-border text-adega-muted hover:text-adega-text'
                      }`}
                    >
                      {item.name}
                    </button>
                  ))}
                </div>
              ) : null}
              <p className="text-xs text-adega-muted">
                Toque na nova posição. A posição atual fica livre automaticamente.
                {cellars && cellars.length > 1 ? ' Dá para mudar de adega nos botões acima.' : ''}
              </p>
              <CellarGrid
                cellar={cellar}
                onSelecionarVaga={async (shelf, position) => {
                  // A adega vai junto: mudar só a prateleira deixaria a garrafa
                  // contada numa adega e posicionada em outra.
                  await atualizar.mutateAsync({ cellarId: cellar.id, shelfId: shelf.id, position });
                  setMovendo(false);
                  setAdegaDoMover(null);
                }}
              />
              {/* Sem isto, garrafa que caiu na adega errada ficava presa lá: o
                  "mover" só oferece vagas, e a fila é justamente o lugar de quem
                  não tem vaga. */}
              <button
                type="button"
                className="btn-secondary w-full"
                disabled={atualizar.isPending}
                onClick={async () => {
                  await atualizar.mutateAsync({ cellarId: null, shelfId: null, position: null });
                  setMovendo(false);
                  setAdegaDoMover(null);
                }}
              >
                📦 Tirar da adega · esperar vaga
              </button>
              {atualizar.error ? <ErrorBox erro={atualizar.error} /> : null}
            </div>
          ) : null}

          {consumindo ? (
            <div className="card space-y-3">
              <p className="text-sm">
                {ocupaVaga
                  ? 'Marcar como bebida libera a posição na adega, mas mantém a garrafa no histórico.'
                  : 'Esta garrafa não ocupa posição em adega nenhuma: marcar como bebida tira ela da fila e mantém no histórico.'}
              </p>

              <div>
                <label className="label" htmlFor="consumida-em">
                  Quando você bebeu?
                </label>
                <input
                  id="consumida-em"
                  type="date"
                  className="field"
                  value={quando}
                  max={hoje()}
                  onChange={(e) => setQuando(e.target.value)}
                />
              </div>

              <div>
                <span className="label">Que nota você dá? (opcional)</span>
                <div className="flex gap-1">
                  {[1, 2, 3, 4, 5].map((valor) => (
                    <button
                      key={valor}
                      type="button"
                      aria-label={`${valor} estrelas`}
                      onClick={() => setNota(nota === valor ? null : valor)}
                      className={`text-2xl transition ${
                        nota !== null && valor <= nota ? 'opacity-100' : 'opacity-30'
                      }`}
                    >
                      ★
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="label" htmlFor="comentario">
                  O que você achou? (opcional)
                </label>
                <textarea
                  id="comentario"
                  rows={2}
                  className="field"
                  value={comentario}
                  onChange={(e) => setComentario(e.target.value)}
                  placeholder="Com o que combinou, como estava, com quem você tomou…"
                />
              </div>

              <button
                type="button"
                className="btn-primary w-full"
                disabled={consumir.isPending}
                onClick={async () => {
                  await consumir.mutateAsync({
                    consumedAt: momentoDoConsumo(quando),
                    rating: nota,
                    note: comentario.trim() || null,
                  });
                  setConsumindo(false);
                  setAcabouDeBeber(true);
                }}
              >
                {consumir.isPending ? 'Registrando…' : 'Confirmar'}
              </button>

              {consumir.error ? <ErrorBox erro={consumir.error} /> : null}
            </div>
          ) : null}
        </section>
      ) : null}

      {/* Separado e discreto de propósito: é a ação irreversível da tela, e não
          pode ser confundida com "Bebi esta", que guarda histórico. */}
      <section className="border-t border-adega-border pt-4">
        {confirmandoExclusao ? (
          <div className="space-y-3 rounded-xl border border-red-900/60 bg-red-950/30 p-4">
            <p className="text-sm font-semibold text-red-200">Excluir “{nomeCompleto(bottle)}”?</p>
            <p className="text-sm text-adega-muted">
              Apaga a garrafa e as fotos de vez. Use só para cadastro errado — se você bebeu,
              prefira “Bebi esta”, que mantém o histórico.
            </p>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                className="btn-secondary"
                disabled={excluir.isPending}
                onClick={() => setConfirmandoExclusao(false)}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="btn bg-red-700 text-white hover:bg-red-600"
                disabled={excluir.isPending}
                onClick={async () => {
                  await excluir.mutateAsync();
                  navigate(bottle.cellarId ? `/adega/${bottle.cellarId}` : '/', { replace: true });
                }}
              >
                {excluir.isPending ? 'Excluindo…' : 'Excluir'}
              </button>
            </div>
            {excluir.error ? <ErrorBox erro={excluir.error} /> : null}
          </div>
        ) : (
          <button
            type="button"
            className="btn-ghost w-full text-red-300/80 hover:text-red-300"
            onClick={() => setConfirmandoExclusao(true)}
          >
            Excluir garrafa cadastrada errada
          </button>
        )}
      </section>
    </div>
  );
}
